const { debounce } = window.BAO.Utils
const KeenSlider = window.KeenSlider

const { setInitialised, useArrows } = window.BAO.Carousel.plugins

class SplitCollectionTabs extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()

    this.carousels = new Map()
    this.setupCarousels()
  }

  disconnectedCallback () {
    super.disconnectedCallback()
    this.destroyCarousels()
  }

  setupListeners () {
    super.setupListeners()

    this.handleClick = this.handleClick.bind(this)
    this.handleMouseOver = this.handleMouseOver.bind(this)

    this.listeners.add(this, 'click', this.handleClick)
    this.listeners.add(this, 'mouseover', this.handleMouseOver)

    this.listeners.add(window, 'resize', debounce(() => {
      this.handleResize()
    }, 500))
  }

  destroyCarousels () {
    this.carousels.forEach((carousel) => {
      carousel.destroy()
    })

    this.carousels.clear()
  }

  handleResize () {
    this.carousels.forEach((carousel) => {
      carousel.update()
    })
  }

  getPanelEls (panel) {
    return this.getElements(
      ['slides', 'slide', 'next', 'previous', 'productSelects'],
      this._elIdentifier,
      { context: panel },
    )
  }

  setupCarousels () {
    if (!this.els?.panel?.exists) return

    this.els.panel.elements.forEach((panel) => {
      const tabId = panel.dataset.tabId
      const panelEls = this.getPanelEls(panel)

      if (!panelEls.slides?.exists || !panelEls.slide?.exists) return

      const slidesEl = panelEls.slides.element
      const carouselPerView = slidesEl.dataset.carouselPerView || 1.15
      const carouselSpacing = slidesEl.dataset.carouselSpacing || '0'
      const carouselSpacingDesktop = slidesEl.dataset.carouselSpacingDesktop || carouselSpacing

      const carousel = new KeenSlider(
        slidesEl,
        {
          selector: panelEls.slide.selector,
          loop: false,
          mode: 'snap',
          slides: {
            perView: Number(carouselPerView),
            spacing: parseInt(carouselSpacing, 10),
          },
          breakpoints: {
            '(min-width: 768px)': {
              slides: {
                perView: 2,
                spacing: parseInt(carouselSpacingDesktop, 10),
              },
            },
          },
        },
        [
          setInitialised(),
          useArrows({
            next: panelEls.next?.element,
            previous: panelEls.previous?.element,
          }),
        ],
      )

      this.carousels.set(tabId, carousel)
    })
  }

  handleMouseOver (event) {
    const gridLink = event.target.closest(this.els?.productSelectGridLink?.selector)

    if (!gridLink) return

    const productSelect = gridLink.closest(this.els?.productSelect?.selector)

    if (!productSelect) return

    this.setProductSelectTitle(productSelect, {
      href: gridLink.href,
      title: gridLink.getAttribute('aria-label'),
    })
  }

  setProductSelectTitle (productSelect, { href, title }) {
    if (!href || !title) return

    const titleEl = productSelect.querySelector(this.els.productSelectTitle.selector)

    if (!titleEl) return

    titleEl.href = href
    titleEl.textContent = title
  }

  resetProductSelectTitle (productSelect) {
    const titleEl = productSelect.querySelector(this.els.productSelectTitle.selector)

    if (!titleEl) return

    const { defaultTitle, defaultUrl } = titleEl.dataset

    if (defaultTitle) {
      titleEl.textContent = defaultTitle
    }

    if (defaultUrl) {
      titleEl.href = defaultUrl
    }
  }

  handleClick (event) {
    const tabTrigger = event.target.closest(this.els?.tab?.selector)

    if (tabTrigger) {
      this.switchTab(tabTrigger)
      return
    }

    const closeTrigger = event.target.closest(this.els?.productSelectClose?.selector)

    if (closeTrigger) {
      const productSelect = closeTrigger.closest(this.els?.productSelect?.selector)
      this.resetProductSelect(productSelect)
      return
    }

    const expandTrigger = event.target.closest(this.els?.productSelectExpand?.selector)

    if (expandTrigger) {
      this.toggleProductSelect(expandTrigger)
    }
  }

  switchTab (tabTrigger) {
    const tabId = tabTrigger.dataset.tabId

    this.els.tab?.elements?.forEach((tab) => {
      const isActive = tab.dataset.tabId === tabId

      tab.classList.toggle('is-active', isActive)
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false')
    })

    this.els.panel?.elements?.forEach((panel) => {
      const isActive = panel.dataset.tabId === tabId

      panel.classList.toggle('is-active', isActive)

      if (isActive) {
        panel.removeAttribute('hidden')
        this.carousels.get(tabId)?.update()
      } else {
        panel.setAttribute('hidden', '')
        const panelEls = this.getPanelEls(panel)
        this.collapseAllProductSelects(panelEls.productSelects?.element || panel)
      }
    })
  }

  toggleProductSelect (expandTrigger) {
    const productSelect = expandTrigger.closest(this.els?.productSelect?.selector)

    if (!productSelect) return

    if (productSelect.classList.contains('is-expanded')) {
      this.resetProductSelect(productSelect)
      return
    }

    const productSelects = productSelect.closest(this.els?.productSelects?.selector)

    this.collapseAllProductSelects(productSelects || this, productSelect)

    productSelect.classList.add('is-expanded')
    productSelects?.classList.add('is-expanded')
    expandTrigger.setAttribute('aria-expanded', 'true')
  }

  collapseAllProductSelects (context, exceptProductSelect = null) {
    if (!this.els?.productSelect?.exists) return

    const productSelectEls = window.BAO.Utils.getElements(this.els.productSelect.selector, {
      context,
    })

    productSelectEls.forEach((productSelect) => {
      if (productSelect === exceptProductSelect) return

      this.resetProductSelect(productSelect)
    })
  }

  resetProductSelect (productSelect) {
    if (!productSelect) return

    const expandTrigger = productSelect.querySelector(this.els.productSelectExpand.selector)
    const productSelects = productSelect.closest(this.els?.productSelects?.selector)

    this.resetProductSelectTitle(productSelect)

    productSelect.classList.remove('is-expanded')
    expandTrigger?.setAttribute('aria-expanded', 'false')

    if (productSelects && !productSelects.querySelector(`${this.els.productSelect.selector}.is-expanded`)) {
      productSelects.classList.remove('is-expanded')
    }
  }
}

if (!customElements.get('split-collection-tabs')) {
  customElements.define('split-collection-tabs', SplitCollectionTabs)
}
